import type React from 'react';
import { Button, Divider, Modal } from '@zillow/constellation-10';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import type { ClickstreamTriggerObjectOnboardingScreen } from '../analytics/events';
import { emitOnboardingWizardNextEvent } from '../analytics/events';

export interface WizardNavigationProps {
  goToNextScreen: () => void;
  goToPreviousScreen: () => void;
  goToScreen: (screenIndex: number) => void;
  currentScreen: number;
  totalScreens: number;
  isFirstScreen: boolean;
  isLastScreen: boolean;
}

export interface WizardScreen {
  id?: string; // Unique identifier for URL navigation
  header?: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode | ((navigation: WizardNavigationProps) => React.ReactNode);
  backButtonText?: string;
  nextButtonText?: string;
  isNextEnabled?: boolean | (() => boolean);
  showFooter?: boolean;
  nextRoute?: string;
  nextAction?: () => void; // Custom action to execute instead of default navigation
  clickstreamTriggerObject?: ClickstreamTriggerObjectOnboardingScreen;
}

export interface WizardWrapperProps {
  screens: Array<WizardScreen>;
  defaultHeader?: React.ReactNode;
  defaultBackButtonText?: string;
  defaultNextButtonText?: string;
  initialScreen?: number;
}

export default function WizardWrapper({
  screens,
  defaultHeader,
  defaultBackButtonText = 'Back',
  defaultNextButtonText = 'Next',
  initialScreen = 0,
}: WizardWrapperProps) {
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const router = useRouter();

  const currentScreenData = screens[currentScreen];
  const headerToShow = currentScreenData?.header ?? defaultHeader;

  // Helper function to find screen index by ID
  const findScreenById = (id: string): number => {
    const index = screens.findIndex((screen) => screen.id === id);
    return index >= 0 ? index : initialScreen;
  };

  // Helper function to parse URL for screen navigation
  const getScreenFromUrl = (): number => {
    if (typeof window === 'undefined') return initialScreen;

    // Check query parameter (e.g., ?step=intro)
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');
    if (stepParam) {
      const screenIndex = findScreenById(stepParam);
      if (screenIndex !== initialScreen || screens[0]?.id === stepParam) {
        return screenIndex;
      }
    }

    return initialScreen;
  };

  const updateUrl = (screenIndex: number) => {
    const screen = screens[screenIndex];
    const currentUrl = new URL(window.location.href);

    if (screen?.id) {
      currentUrl.searchParams.set('step', screen.id);
    } else {
      currentUrl.searchParams.delete('step');
    }

    window.history.pushState({ wizardScreen: screenIndex }, '', currentUrl.toString());
  };

  const goToNextScreen = () => {
    // Emit analytics event if clickstream trigger is provided
    if (currentScreenData?.clickstreamTriggerObject) {
      emitOnboardingWizardNextEvent(
        currentScreenData.clickstreamTriggerObject as ClickstreamTriggerObjectOnboardingScreen,
      );
    }

    // Check if current screen has a custom route
    if (currentScreenData?.nextRoute) {
      router.push(currentScreenData.nextRoute as Parameters<typeof router.push>[0]);
      return;
    }

    // Check if current screen has a custom nextAction
    if (currentScreenData?.nextAction) {
      currentScreenData.nextAction();
      return;
    }

    // Default behavior: go to next screen
    if (currentScreen < screens.length - 1) {
      const nextScreen = currentScreen + 1;
      setCurrentScreen(nextScreen);
      updateUrl(nextScreen);
    }
  };

  const goToPreviousScreen = () => {
    if (currentScreen > 0) {
      const prevScreen = currentScreen - 1;
      setCurrentScreen(prevScreen);
      updateUrl(prevScreen);
    }
  };

  const goToScreen = (screenIndex: number) => {
    if (screenIndex >= 0 && screenIndex < screens.length) {
      setCurrentScreen(screenIndex);
      updateUrl(screenIndex);
    }
  };

  const isFirstScreen = currentScreen === 0;
  const isLastScreen = currentScreen === screens.length - 1;

  // Initialize screen from URL on mount
  useEffect(() => {
    const screenFromUrl = getScreenFromUrl();
    if (screenFromUrl !== initialScreen) {
      setCurrentScreen(screenFromUrl);
    } else {
      // Initialize URL with current screen if it has an ID
      if (screens[initialScreen]?.id && typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('step', screens[initialScreen].id);
        window.history.replaceState({ wizardScreen: initialScreen }, '', currentUrl.toString());
      }
    }
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const screenFromUrl = getScreenFromUrl();
      if (screenFromUrl !== currentScreen) {
        setCurrentScreen(screenFromUrl);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentScreen]);

  const navigationProps: WizardNavigationProps = {
    goToNextScreen,
    goToPreviousScreen,
    goToScreen,
    currentScreen,
    totalScreens: screens.length,
    isFirstScreen,
    isLastScreen,
  };

  const getDefaultFooter = () => {
    const backButtonText = currentScreenData?.backButtonText ?? defaultBackButtonText;
    const nextButtonText = currentScreenData?.nextButtonText ?? defaultNextButtonText;

    // Determine if next button should be enabled
    let isNextButtonEnabled = true;

    // If screen has a nextRoute or nextAction, check isNextEnabled if provided
    if (currentScreenData?.nextRoute || currentScreenData?.nextAction) {
      if (currentScreenData?.isNextEnabled !== undefined) {
        if (typeof currentScreenData.isNextEnabled === 'function') {
          isNextButtonEnabled = currentScreenData.isNextEnabled();
        } else {
          isNextButtonEnabled = currentScreenData.isNextEnabled;
        }
      }
    } else {
      // Default wizard navigation logic
      isNextButtonEnabled = !isLastScreen;
      if (currentScreenData?.isNextEnabled !== undefined) {
        if (typeof currentScreenData.isNextEnabled === 'function') {
          isNextButtonEnabled = isNextButtonEnabled && currentScreenData.isNextEnabled();
        } else {
          isNextButtonEnabled = isNextButtonEnabled && currentScreenData.isNextEnabled;
        }
      }
    }

    return (
      <>
        {currentScreen > 0 && (
          <Button
            onClick={goToPreviousScreen}
            disabled={isFirstScreen}
            tone="neutral"
            data-testid="back-button"
            css={{
              display: 'flex',
              flex: 1,
              marginRight: '{spacing.400}',
              '@media (min-width: 1024px)': {
                display: 'block',
                flex: 'none',
                marginRight: '0',
                minWidth: '164px',
                textAlign: 'center',
              },
            }}
          >
            {backButtonText}
          </Button>
        )}
        <Button
          onClick={goToNextScreen}
          disabled={!isNextButtonEnabled}
          tone="brand"
          emphasis="filled"
          data-testid="next-button"
          css={{
            display: 'flex',
            flex: 1,
            '@media (min-width: 1024px)': {
              display: 'block',
              flex: 'none',
              marginLeft: currentScreen > 0 ? '0' : 'auto',
              minWidth: '164px',
              textAlign: 'center',
            },
          }}
        >
          {nextButtonText}
        </Button>
      </>
    );
  };
  return (
    <Modal.Root size="fullScreen" open={!!true}>
      <Modal.Portal>
        <Modal.Backdrop data-panda-theme="zillow" css={{ animation: 'none' }}>
          <Modal.Content css={{ animation: 'none' }}>
            {headerToShow && (
              <>
                <Modal.Header
                  css={{ paddingLeft: { base: '16px', lg: '96px' }, paddingRight: { base: '16px', lg: '96px' } }}
                >
                  {headerToShow}
                </Modal.Header>
                <Divider />
              </>
            )}
            <Modal.Body css={{ padding: { base: '{spacing.default}', lg: '32px 96px 32px 160px' } }}>
              {currentScreenData?.body}
            </Modal.Body>
            {currentScreenData?.showFooter !== false && (
              <>
                <Divider />
                <Modal.Footer
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingLeft: { base: '{spacing.default}', lg: '{spacing.2400}' },
                    paddingRight: { base: '{spacing.default}', lg: '{spacing.2400}' },
                    paddingBottom: { base: '{spacing.default}', lg: 'auto' },
                  }}
                >
                  {currentScreenData?.footer
                    ? typeof currentScreenData.footer === 'function'
                      ? currentScreenData.footer(navigationProps)
                      : currentScreenData.footer
                    : getDefaultFooter()}
                </Modal.Footer>
              </>
            )}
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
